import React, { useEffect } from "react";
import { Handle, Position, useUpdateNodeInternals } from "reactflow";
import {
  QuestionTypes,
  QuestionTypeLabelKeys,
  DEFAULT_LIST_OPTIONS,
} from "../../utils/constants";
import { Handle as HandleId, listHandleId } from "../../utils/flowSchema";
import { useResources } from "../../context/diagnosticFlowResources";
import { NodeIcons, QuestionTypeIcons } from "../nodeIcons";

const typeConfig = {
  [QuestionTypes.FREE_TEXT]: {
    bg: "#e3f2fd",
    border: "#1976d2",
    badge: "#1976d2",
    gradient: "linear-gradient(135deg, #e3f2fd, #bbdefb)",
  },
  [QuestionTypes.YES_NO]: {
    bg: "#fff3e0",
    border: "#f57c00",
    badge: "#f57c00",
    gradient: "linear-gradient(135deg, #fff3e0, #ffe0b2)",
  },
  [QuestionTypes.TIME_DURATION]: {
    bg: "#f3e5f5",
    border: "#7b1fa2",
    badge: "#7b1fa2",
    gradient: "linear-gradient(135deg, #f3e5f5, #e1bee7)",
  },
  [QuestionTypes.LIST]: {
    bg: "#e0f2f1",
    border: "#00897b",
    badge: "#00897b",
    gradient: "linear-gradient(135deg, #e0f2f1, #b2dfdb)",
  },
};

const handleStyle = {
  width: "10px",
  height: "10px",
  border: "2px solid",
};

const LIST_OPTION_SLOT_WIDTH = 88;
const LIST_OPTION_LABEL_GAP = 10;
const LIST_MIN_WIDTH = 260;

export default function QuestionNode({ id, data, selected }) {
  const resources = useResources();
  const questionType = data.questionType || QuestionTypes.FREE_TEXT;
  const config = typeConfig[questionType] || typeConfig[QuestionTypes.FREE_TEXT];
  const TypeIcon = QuestionTypeIcons[questionType] || QuestionTypeIcons[QuestionTypes.FREE_TEXT];
  const hasError = data._invalid;
  const isYesNo = questionType === QuestionTypes.YES_NO;
  const isList = questionType === QuestionTypes.LIST;
  const listOptions = data.listOptions || DEFAULT_LIST_OPTIONS;
  const listWidth = Math.max(LIST_MIN_WIDTH, listOptions.length * LIST_OPTION_SLOT_WIDTH);

  // Re-measure handles on count/type change so edges don't anchor to stale positions.
  const updateNodeInternals = useUpdateNodeInternals();
  useEffect(() => {
    updateNodeInternals(id);
  }, [id, questionType, listOptions.length, updateNodeInternals]);

  const containerStyle = {
    borderRadius: "10px",
    border: `2px solid ${hasError ? "#e53935" : selected ? "#1a73e8" : config.border}`,
    background: "#fff",
    minWidth: isList ? `${listWidth}px` : "220px",
    maxWidth: isList ? `${listWidth}px` : "280px",
    boxShadow: selected
      ? "0 0 0 3px #1a73e8, 0 6px 16px rgba(0,0,0,0.12)"
      : hasError
      ? "0 0 0 2px #e53935, 0 4px 12px rgba(229,57,53,0.2)"
      : "0 2px 10px rgba(0,0,0,0.08)",
    fontSize: "13px",
    overflow: "hidden",
    transition: "box-shadow 0.2s ease, border-color 0.2s ease",
    cursor: "pointer",
  };

  const headerStyle = {
    background: hasError ? "#ffebee" : config.gradient,
    padding: "8px 14px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    borderBottom: `1px solid ${hasError ? "#e5393533" : config.border + "22"}`,
  };

  const badgeStyle = {
    background: hasError ? "#e53935" : config.badge,
    color: "#fff",
    padding: "2px 10px",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.3px",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  const bodyStyle = {
    padding: "12px 14px",
    color: "#333",
    lineHeight: "1.5",
    fontWeight: 500,
  };

  const renderOutputHandles = () => {
    if (isYesNo) {
      return (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "0 14px 10px",
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "0.5px",
              textTransform: "uppercase",
            }}
          >
            <span style={{ color: "#43a047" }}>{resources["Yes"]}</span>
            <span style={{ color: "#e53935" }}>{resources["No"]}</span>
          </div>
          <Handle
            type="source"
            position={Position.Bottom}
            id={HandleId.YES}
            style={{
              ...handleStyle,
              background: "#fff",
              borderColor: "#43a047",
              left: "25%",
              bottom: "-6px",
            }}
          />
          <Handle
            type="source"
            position={Position.Bottom}
            id={HandleId.NO}
            style={{
              ...handleStyle,
              background: "#fff",
              borderColor: "#e53935",
              left: "75%",
              bottom: "-6px",
            }}
          />
        </>
      );
    }

    if (isList) {
      const count = listOptions.length;
      return (
        <>
          <div style={{ position: "relative", height: "22px", margin: "0 0 10px" }}>
            {listOptions.map((opt, i) => {
              const pct = ((i + 1) / (count + 1)) * 100;
              const text = opt || `${resources["DiagnosticFlowOption"]} ${i + 1}`;
              return (
                <span
                  key={i}
                  title={text}
                  style={{
                    position: "absolute",
                    left: `${pct}%`,
                    transform: "translateX(-50%)",
                    maxWidth: `${LIST_OPTION_SLOT_WIDTH - LIST_OPTION_LABEL_GAP}px`,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    background: `${config.border}14`,
                    color: config.border,
                    padding: "2px 8px",
                    borderRadius: "8px",
                    fontSize: "10px",
                    fontWeight: 600,
                    letterSpacing: "0.3px",
                    boxSizing: "border-box",
                  }}
                >
                  {text}
                </span>
              );
            })}
          </div>
          {listOptions.map((opt, i) => {
            const pct = ((i + 1) / (count + 1)) * 100;
            return (
              <Handle
                key={`list-${i}`}
                type="source"
                position={Position.Bottom}
                id={listHandleId(i)}
                style={{
                  ...handleStyle,
                  background: "#fff",
                  borderColor: config.border,
                  left: `${pct}%`,
                  bottom: "-6px",
                }}
              />
            );
          })}
        </>
      );
    }

    return (
      <Handle
        type="source"
        position={Position.Bottom}
        id={HandleId.CONTINUE}
        style={{
          ...handleStyle,
          background: "#fff",
          borderColor: config.border,
          bottom: "-6px",
        }}
      />
    );
  };

  return (
    <div style={containerStyle}>
      <Handle
        type="target"
        position={Position.Top}
        style={{
          ...handleStyle,
          background: "#fff",
          borderColor: config.border,
          top: "-6px",
        }}
      />

      <div style={headerStyle}>
        <TypeIcon style={{ fontSize: "16px", color: config.border, flexShrink: 0 }} />
        <span style={badgeStyle} title={resources[QuestionTypeLabelKeys[questionType]]}>
          {resources[QuestionTypeLabelKeys[questionType]]}
        </span>
        {hasError && (
          <span
            style={{ marginLeft: "auto", display: "flex" }}
            title={resources["DiagnosticFlowHasValidationErrors"]}
          >
            <NodeIcons.WARNING style={{ fontSize: "16px", color: "#e53935" }} />
          </span>
        )}
      </div>

      <div style={bodyStyle}>
        {data.label ? (
          data.label
        ) : (
          <span style={{ color: "#B0BEC5", fontStyle: "italic", fontWeight: 400 }}>
            {resources["DiagnosticFlowNewQuestion"]}
          </span>
        )}
      </div>

      {renderOutputHandles()}
    </div>
  );
}
